<script lang="ts" setup>
import { ref, computed, watch, toRef } from "vue";
import { useProjectWorkflows, useWizardSequence, useUpdateWizardSequence } from "@/queries/projects";
import type { Project, ProjectWorkflowView } from "@/types";

interface Props {
  projectId: string;
  project?: Project | null;
}

const props = defineProps<Props>();

const projectIdRef = toRef(props, "projectId");

const { projectWorkflows, projectWorkflowsLoading } = useProjectWorkflows(projectIdRef, computed(() => !!props.projectId));
const { wizardSequence, wizardSequenceLoading } = useWizardSequence(projectIdRef);
const { updateWizardSequenceAsync, updateWizardSequencePending } = useUpdateWizardSequence(projectIdRef);

// One-shot guard for the auto-seed below — we want to write the default ordering exactly once
// per editor mount, never overwrite an already-persisted sequence.
const autoSeeded = ref(false);

// Subscribers only ever consume published versions with configured wizard steps,
// so the editor only surfaces workflows that have been published at least once AND have
// at least one wizard step. Use `version` (which is "Draft" only when the workflow has
// never been published) rather than `status` — `status` flips back to "Draft" mid-edit,
// which would incorrectly hide an already-published workflow while the publisher iterates.
const publishedWorkflows = computed(() =>
  projectWorkflows.value.filter(
    (w) =>
      !!w.version &&
      w.version !== "Draft" &&
      (w.wizardStepCount ?? 0) > 0
  )
);

// Local ordered list — initialised from persisted sequence, falls back to workflow order
const orderedWorkflows = ref<ProjectWorkflowView[]>([]);
const savedIndicator = ref(false);

// Build ordered list whenever workflows or sequence loads
watch(
  [publishedWorkflows, wizardSequence],
  ([workflows, sequence]) => {
    if (!workflows.length) {
      orderedWorkflows.value = [];
      return;
    }

    if (sequence.length) {
      // Sort by the persisted sequence; append any workflows not yet in the sequence at the end
      const inSequence = sequence
        .map((id) => workflows.find((w) => w.id === id))
        .filter((w): w is ProjectWorkflowView => !!w);
      const notInSequence = workflows.filter((w) => !sequence.includes(w.id));
      orderedWorkflows.value = [...inSequence, ...notInSequence];
    } else {
      orderedWorkflows.value = [...workflows];

      // Persist the default ordering on first render once both queries have settled. Without this,
      // a publisher who only ever views the tab (never clicks the arrows) leaves
      // PublisherData.WizardSequence empty — and the next product publish snapshots nothing into
      // ProductVersion.WizardSequenceSnapshot, so subscribers see "No wizard steps are defined…".
      // Guarded by `autoSeeded` so we don't fire repeatedly as the queries re-fetch.
      if (
        !autoSeeded.value &&
        !projectWorkflowsLoading.value &&
        !wizardSequenceLoading.value
      ) {
        autoSeeded.value = true;
        persist();
      }
    }
  },
  { immediate: true }
);

const loading = computed(() => projectWorkflowsLoading.value || wizardSequenceLoading.value);

async function moveUp(index: number) {
  if (index <= 0) return;
  const arr = orderedWorkflows.value;
  [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
  await persist();
}

async function moveDown(index: number) {
  if (index >= orderedWorkflows.value.length - 1) return;
  const arr = orderedWorkflows.value;
  [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
  await persist();
}

async function persist() {
  await updateWizardSequenceAsync(orderedWorkflows.value.map((w) => w.id));
  savedIndicator.value = true;
  setTimeout(() => (savedIndicator.value = false), 1500);
}
</script>

<template>
  <div class="tab-pane fade show active">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-start mb-4">
      <div>
        <h2 class="card-title mb-1">Wizard Sequence</h2>
        <p class="text-muted mb-0">
          Set the order workflows appear in the subscriber setup wizard.
        </p>
      </div>
      <small v-if="updateWizardSequencePending" class="text-muted">
        <i class="bi bi-arrow-repeat spin me-1"></i>Saving…
      </small>
      <small v-else-if="savedIndicator" class="text-success">
        <i class="bi bi-check-circle me-1"></i>Saved
      </small>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5 text-muted">
      <div class="spinner-border spinner-border-sm me-2"></div>
      Loading workflows…
    </div>

    <!-- Empty -->
    <div v-else-if="!orderedWorkflows.length" class="text-center py-5 text-muted">
      <i class="bi bi-diagram-3 display-5"></i>
      <p v-if="!projectWorkflows.length" class="mt-3">No workflows found for this project.</p>
      <p v-else class="mt-3">No workflows are eligible for the wizard yet.</p>
      <p v-if="!projectWorkflows.length" class="small">
        Add workflows in the builder first, then configure their wizard steps and publish them.
      </p>
      <p v-else class="small">
        Only published workflows with at least one wizard step appear here.
        Configure wizard steps in the builder and publish to add a workflow to the sequence.
      </p>
    </div>

    <!-- Workflow list -->
    <div v-else class="card">
      <div class="card-body pb-0">
        <div class="d-flex justify-content-between align-items-center mb-2 gap-3">
          <h3 class="mb-0">
            <i class="bi bi-diagram-3 me-2"></i>
            Workflows
            <span class="badge bg-primary ms-2">{{ orderedWorkflows.length }}</span>
          </h3>
          <small class="text-muted d-none d-md-inline">Drag to reorder or use the arrows</small>
        </div>
      </div>
      <div class="card-body p-0">
        <div
          v-for="(workflow, index) in orderedWorkflows"
          :key="workflow.id"
          class="d-flex align-items-center p-3 border-bottom"
        >
          <!-- Position badge -->
          <span class="badge bg-secondary rounded-pill me-3" style="min-width: 2rem">
            {{ index + 1 }}
          </span>

          <!-- Icon -->
          <i class="bi bi-diagram-3 text-muted me-3"></i>

          <!-- Info -->
          <div class="flex-grow-1 min-w-0">
            <div class="d-flex align-items-center gap-2">
              <strong class="text-truncate">{{ workflow.name }}</strong>
              <span
                v-if="workflow.version"
                class="badge bg-success"
                title="Latest published version — this is what would land in the next product publish"
              >{{ workflow.version }}</span>
            </div>
            <div class="d-flex align-items-center gap-3 mt-1">
              <small class="text-muted">
                <i class="bi bi-magic me-1"></i>
                {{ workflow.wizardStepCount }} wizard step{{ workflow.wizardStepCount === 1 ? "" : "s" }}
              </small>
            </div>
          </div>

          <!-- Move buttons -->
          <div class="d-flex gap-1 ms-3">
            <button
              class="bb-btn-secondary-cta btn-sm"
              :disabled="index === 0"
              @click="moveUp(index)"
              title="Move up"
            >
              <i class="bi bi-arrow-up"></i>
            </button>
            <button
              class="bb-btn-secondary-cta btn-sm"
              :disabled="index === orderedWorkflows.length - 1"
              @click="moveDown(index)"
              title="Move down"
            >
              <i class="bi bi-arrow-down"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
@keyframes spin {
  to { transform: rotate(360deg); }
}
.spin {
  display: inline-block;
  animation: spin 0.75s linear infinite;
}
</style>
